import { useCallback, useEffect } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";

import FormFieldViewText from "components/FormFields/ViewText";
import FormFieldSpace from "components/FormFields/Space";
import FormFieldTags from "components/FormFields/Tags";
import Button from "ds/components/Button";
import FormField from "ds/components/Form/Field";
import FormFieldHidden from "ds/components/Form/Field/Hidden";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalFooter from "ds/components/FullScreenModal/Footer";
import Input from "ds/components/Input";
import SecretInput from "ds/components/SecretInput";
import Select from "ds/components/Select";
import Textarea from "ds/components/Textarea";
import useTypedContext from "hooks/useTypedContext";
import { BlobStorageSubtype, VcsProvider } from "types/generated";
import { stringIsRequired, validateRequiredURL } from "utils/formValidators";
import { AccountContext } from "views/AccountWrapper";

import VCSIntegrationTypeFieldControl from "../../components/IntegrationTypeFieldControl";
import VCSIntegrationsDocumentationButton from "../components/DocumentationButton";
import useCloseFullScreenModal from "../useCloseFullScreenModal";
import {
  getBlobStorageSubtypeOptions,
  getFormDefaultValues,
  mapCreateBlobStorageIntegrationInputValues,
  mapUpdateBlobStorageIntegrationInputValues,
} from "./helpers";
import { ManageBlobStorageFormFields } from "./types";
import useCheckBlobStorageHasDefaultIntegration from "./useCheckBlobStorageHasDefaultIntegration";
import useCreateBlobStorageIntegration from "./useCreateBlobStorageIntegration";
import useUpdateBlobStorageIntegration from "./useUpdateBlobStorageIntegration";
import { getSecretAccessKeyValidator, validateBaseURI } from "./validators";
import { BlobStorageIntegrationData } from "./Edit/gql";

type VCSIntegrationsManageBlobStorageFormProps = {
  integration?: BlobStorageIntegrationData;
  onIsDirtyChange: (value: boolean) => void;
};

const VCSIntegrationsManageBlobStorageForm = ({
  integration,
  onIsDirtyChange,
}: VCSIntegrationsManageBlobStorageFormProps) => {
  const isEditMode = !!integration;
  const { viewer } = useTypedContext(AccountContext);

  const { handleOnClose } = useCloseFullScreenModal();

  const manageBlobStorageForm = useForm<ManageBlobStorageFormFields>({
    defaultValues: getFormDefaultValues(integration),
    mode: "onChange",
  });

  const {
    control,
    register,
    formState: { errors, isDirty },
    handleSubmit,
    setValue,
    getValues,
    trigger,
  } = manageBlobStorageForm;

  const { createBlobStorageIntegration, loading: createLoading } =
    useCreateBlobStorageIntegration();
  const { updateBlobStorageIntegration, loading: updateLoading } =
    useUpdateBlobStorageIntegration();

  const { hasDefaultIntegration, loading: checkDefaultIntegrationLoading } =
    useCheckBlobStorageHasDefaultIntegration(isEditMode);

  const withIntegrationTypeControl = !isEditMode && viewer.admin;
  const withSpaceControl = !withIntegrationTypeControl && !integration?.isDefault;

  const subtype = useWatch({ control, name: "subtype" });
  const isAwsS3 = subtype === BlobStorageSubtype.AwsS3;

  // Read at validation time - a render-time capture would lag one keystroke behind.
  const validateSecretAccessKey = (value: string) =>
    getSecretAccessKeyValidator({
      isAwsS3,
      isEditMode,
      accessKeyId: getValues("accessKeyId"),
      previousAccessKeyId: integration?.accessKeyId ?? "",
    })(value);

  const onSecretHiddenToggle = useCallback(
    (isHidden: boolean) => {
      if (!isHidden) {
        setValue("secretAccessKey", "", { shouldValidate: true });
      } else {
        trigger();
      }
    },
    [setValue, trigger]
  );

  const handleSetup = useCallback(
    () =>
      handleSubmit((formData: ManageBlobStorageFormFields) => {
        if (isEditMode) {
          updateBlobStorageIntegration(
            mapUpdateBlobStorageIntegrationInputValues(formData, integration.id),
            handleOnClose
          );
        } else {
          createBlobStorageIntegration(
            mapCreateBlobStorageIntegrationInputValues(formData),
            handleOnClose
          );
        }
      })(),
    [
      createBlobStorageIntegration,
      handleOnClose,
      integration,
      isEditMode,
      updateBlobStorageIntegration,
      handleSubmit,
    ]
  );

  useEffect(() => {
    onIsDirtyChange(isDirty);
  }, [isDirty, onIsDirtyChange]);

  return (
    <FormProvider {...manageBlobStorageForm}>
      <FullScreenModalBody>
        <FormField
          label="Integration name"
          helperText="The integration name is unique and cannot be changed"
          error={errors?.name?.message}
          noMargin
        >
          {({ ariaInputProps }) => (
            <Input
              placeholder="Enter integration name here"
              error={!!errors?.name}
              {...register("name", {
                validate: stringIsRequired("Integration name field is required."),
              })}
              disabled={isEditMode}
              {...ariaInputProps}
            />
          )}
        </FormField>

        {withIntegrationTypeControl && (
          <VCSIntegrationTypeFieldControl
            hasAlreadyDefaultIntegration={hasDefaultIntegration}
            loading={checkDefaultIntegrationLoading}
            provider={VcsProvider.Blob}
          />
        )}
        {withSpaceControl && <FormFieldSpace />}

        <Controller
          name="subtype"
          control={control}
          render={({ field }) => (
            <FormField
              label="Storage subtype"
              helperText="Decides how Spacelift connects and authenticates. Cannot be changed after creation."
            >
              {({ ariaInputProps }) => (
                <Select
                  value={field.value}
                  items={getBlobStorageSubtypeOptions()}
                  onChange={field.onChange}
                  isDisabled={isEditMode}
                  {...ariaInputProps}
                />
              )}
            </FormField>
          )}
        />

        <FormField
          label="Base URI"
          helperText="The bucket plus an optional prefix, e.g. s3://acme-bucket/acme-infra"
          error={errors?.baseURI?.message}
        >
          {({ ariaInputProps }) => (
            <Input
              placeholder="s3://bucket/optional-prefix"
              error={!!errors?.baseURI}
              {...register("baseURI", { validate: validateBaseURI })}
              {...ariaInputProps}
            />
          )}
        </FormField>

        {isAwsS3 && (
          <>
            <FormField label="Region" error={errors?.region?.message}>
              {({ ariaInputProps }) => (
                <Input
                  placeholder="e.g. us-east-1"
                  error={!!errors?.region}
                  {...register("region", {
                    validate: stringIsRequired("Region field is required."),
                  })}
                  {...ariaInputProps}
                />
              )}
            </FormField>

            <FormField
              label="Role ARN"
              isOptional
              helperText="Role Spacelift assumes to read the bucket. Leave empty for a public, anonymous-read bucket."
              error={errors?.roleArn?.message}
            >
              {({ ariaInputProps }) => (
                <Input
                  placeholder="arn:aws:iam::123456789012:role/spacelift-blob-reader"
                  {...register("roleArn")}
                  {...ariaInputProps}
                />
              )}
            </FormField>

            {isEditMode && (
              <FormFieldViewText
                label="External ID"
                value={integration.externalId}
                withCopy
                tooltipInfo="Use this Spacelift-generated External ID in the assumed role's trust policy."
                tooltipWidthMode="maxWidthSm"
              />
            )}
          </>
        )}

        {!isAwsS3 && (
          <>
            <FormField label="Endpoint URL" error={errors?.endpoint?.message}>
              {({ ariaInputProps }) => (
                <Input
                  placeholder="https://ceph.internal"
                  error={!!errors?.endpoint}
                  {...register("endpoint", { validate: validateRequiredURL() })}
                  {...ariaInputProps}
                />
              )}
            </FormField>

            <FormField
              label="Access key ID"
              isOptional
              helperText="Leave empty (with the secret) for a public, anonymous-read bucket."
              error={errors?.accessKeyId?.message}
            >
              {({ ariaInputProps }) => (
                <Input
                  placeholder="Enter access key ID here"
                  {...register("accessKeyId", {
                    onChange: () => trigger("secretAccessKey"),
                  })}
                  {...ariaInputProps}
                />
              )}
            </FormField>

            {!isEditMode && (
              <FormField
                label="Secret access key"
                isOptional
                error={errors?.secretAccessKey?.message}
              >
                {({ ariaInputProps }) => (
                  <SecretInput
                    placeholder="Enter secret access key here"
                    error={!!errors?.secretAccessKey}
                    {...register("secretAccessKey", { validate: validateSecretAccessKey })}
                    {...ariaInputProps}
                  />
                )}
              </FormField>
            )}

            {isEditMode && (
              <FormFieldHidden
                hiddenPlaceholder="Update secret access key"
                onHiddenToggle={onSecretHiddenToggle}
                label="Secret access key"
                error={errors?.secretAccessKey?.message}
              >
                {() => (
                  <SecretInput
                    placeholder="Enter secret access key here"
                    error={!!errors?.secretAccessKey}
                    {...register("secretAccessKey", { validate: validateSecretAccessKey })}
                  />
                )}
              </FormFieldHidden>
            )}
          </>
        )}

        <FormFieldTags label="Labels" tagName="label" name="labels" isOptional />

        <FormField label="Description" isOptional>
          {({ ariaInputProps }) => (
            <Textarea
              placeholder="Enter description here"
              {...register("description")}
              {...ariaInputProps}
            />
          )}
        </FormField>
      </FullScreenModalBody>
      <FullScreenModalFooter justify="between">
        <VCSIntegrationsDocumentationButton provider={VcsProvider.Blob} />

        <Button onPress={handleSetup} variant="primary" loading={createLoading || updateLoading}>
          {isEditMode ? "Save" : "Set up"}
        </Button>
      </FullScreenModalFooter>
    </FormProvider>
  );
};

export default VCSIntegrationsManageBlobStorageForm;
